import { nanoid } from '@reduxjs/toolkit';
import {
  Ifilter,
  InputFilter,
  FilterOperators,
  FilterConnectors,
  FilterOptions,
  StringKey,
  FilterNames,
  SelectableOption
} from './interfaces';
import { AccountFilterNames } from '../../accounts/filters/fields';
import { UserFilterNames } from '../../users/filters/fields';

export type SearchableField = FilterNames | AccountFilterNames | UserFilterNames;

const isValidValue = (value: InputFilter) => {
  return value !== undefined && value !== null && value !== '';
};

const requireOperands = (value: string): boolean => {
  return value !== FilterOperators.isBlank;
};

const isFilterRanged = (value: string): boolean => {
  return (value === FilterOperators.betweenExclusive || value === FilterOperators.betweenInclusive);
};

export const validateFilters = (filters: Ifilter[]): boolean => {
  for (const filter of filters) {
    if (!isValidValue(filter.field) ||
      !isValidValue(filter.operator) ||
      ((requireOperands(filter.operator)) && !isValidValue(filter.operands[0])) ||
      ((isFilterRanged(filter.operator)) && !isValidValue(filter.operands[1]))
    ) {
      return false;
    }
  }
  return true;
};

const replacer = (key: string, value: InputFilter) => {
  if (key === 'id' || key === 'validateInputs' || (key === 'connector' && value === FilterConnectors.none)) {
    return undefined;
  }
  return value;
};

export const buildFilterQuery = (filters?: Ifilter[], identifier = 'filters'): string => {
  if (!filters || !filters.length) {
    return '';
  }
  return `${identifier}=${JSON.stringify(filters, replacer)}`;
};

export const buildSearchFilters = (searchQuery: string, searchableFields: ReadonlyArray<SearchableField>): Ifilter[] => {
  if (!searchQuery) {
    return [];
  }

  const filters: Ifilter[] = [];
  searchableFields.forEach((field, index) => {
    filters.push({
      field: field,
      operator: 'REGEX',
      operands: [encodeURIComponent(searchQuery)],
      connector: (index > 0) ? FilterConnectors.or : FilterConnectors.none,
      id: nanoid(),
      validateInputs: false
    });
  });
  return filters;
};

export const mergeOptions = (baseArray: FilterOptions[], overrideArray: FilterOptions[]): FilterOptions[] => {
  const result = [...overrideArray, ...baseArray];
  return result.filter((item, pos, arr) => {
    return arr.findIndex(el => el.fieldName === item.fieldName) === pos;
  });
};

export const mapKeyNames = (options: FilterOptions[], keyNames: StringKey): FilterOptions[] => {
  return options.map(option => (
    (keyNames[option.fieldName]) ?
      { ...option, fieldName: keyNames[option.fieldName] } :
      option
  ));
};

export const checkIsFilterForThisPage = (options: SelectableOption[], menuFilters: Ifilter[]): boolean => {
  const activeFilters = menuFilters.map(filter => filter.field);
  const availableOptions = options.map(filter => filter.value);

  return activeFilters.every(filter => availableOptions.includes(filter));
};
